import React from 'react';
import I18n from 'i18n-js';
import { graphql, ChildProps } from 'react-apollo';
import gql from 'graphql-tag';
import track from 'react-tracking';

import { CoreHomePageFeed, MixedFeedRow, reverb_feed_FeedEntry_EntryType } from '../schemas/rql';
import FeedRow, { mixedFeedFragment } from './feed_row';

import { ListingsCollection } from '@reverbdotcom/commons/src/components/listings_collection';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

const FEED_HREF = '/my/feed';

const EXPECTED_NUM = 12;
const MIN_LISTINGS = 4;

const COMPONENT_NAME = 'FeedRow';

type IProps = ChildProps<IUserContext, CoreHomePageFeed.Query, CoreHomePageFeed.Variables>;
/**
 * RQLHomePageFeed is only rendered for logged in users. The hook
 * for that decision is up a layer in the Homepage itself
 */
@track({ componentName: COMPONENT_NAME })
export class RQLHomePageFeed extends React.Component<IProps, {}> {
  static entryIds(props: IProps) {
    const entries = props.data?.me?.feed?.entries ?? [];
    return entries.map(entry => entry.id);
  }

  get entries(): MixedFeedRow.Fragment[] {
    return this.props.data?.me?.feed?.entries ?? [];
  }

  get listings(): MixedFeedRow.ListingInlineFragment[] {
    return this
      .entries
      .filter(entry => entry.type === reverb_feed_FeedEntry_EntryType.LISTING)
      .map(entry => entry.entry as MixedFeedRow.ListingInlineFragment);
  }

  render() {
    const { entries } = this;

    if (!this.props.data.loading && entries.length < MIN_LISTINGS) {
      return null;
    }

    return (
      <section className="scaling-mtb-5">
        <FeedRow
          title={I18n.t('discovery.feed.title')}
          cta={(
            <a href={FEED_HREF}>
              {I18n.t('discovery.homepage.feedCallout.seeMore')}
            </a>
          )}
          expectedCount={EXPECTED_NUM}
          loading={this.props.data.loading}
          singleRow
          entries={entries}
        />
      </section>
    );
  }
}

const connect = graphql<IProps, CoreHomePageFeed.Query, CoreHomePageFeed.Variables>(
  gql`
    query Core_HomePage_Feed(
      $readFromDatabase: Boolean
    ) {
      me {
        uuid
        feed(input: { channel: REVERB, offset: 0, limit: 12, readFromDatabase: $readFromDatabase}) {
          total
          offset
          limit
          entries {
            ...MixedFeedRow
            entry {
              ... on Listing {
                _id
              }
            }
          }
        }
      }
    }
    ${mixedFeedFragment}
    ${ListingsCollection.fragments.listing}
  `,
  {
    options: (props) => {
      return {
        ssr: false,
        variables: {
          readFromDatabase: isExperimentEnabled(props.user, experiments.FEED_POSTGRES_READS),
        },
      };
    },
  },
);

export default withUserContext(connect(RQLHomePageFeed));
